import React, { useState, useEffect } from 'react';

const YouTube = () => {
  // Only use y.com.sb as it's working according to the user
  const invidious_instance = "https://y.com.sb";
  
  const [videoId, setVideoId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [watchHistory, setWatchHistory] = useState([]);
  const [playerError, setPlayerError] = useState(false);
  const [trendingVideos, setTrendingVideos] = useState([]);
  const [isLoadingTrending, setIsLoadingTrending] = useState(false);

  // Load watch history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('webTopOS_youtube_history');
    if (savedHistory) {
      setWatchHistory(JSON.parse(savedHistory));
    }
    
    // Fetch trending videos when component mounts
    fetchTrendingVideos();
  }, []);

  // Fetch trending/popular videos
  const fetchTrendingVideos = async () => {
    setIsLoadingTrending(true);
    try {
      // List of country codes to fetch random trending videos
      const regions = ['US', 'GB', 'CA', 'DE', 'FR', 'JP', 'KR', 'IN', 'BR', 'AU', 'RU', 'MX', 'ES', 'IT', 'NL'];
      // Pick a random region each time refresh is clicked
      const randomRegion = regions[Math.floor(Math.random() * regions.length)];
      
      // Fetch trending videos from Invidious API with a random region
      const response = await fetch(`${invidious_instance}/api/v1/trending?region=${randomRegion}&limit=24`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch trending videos');
      }
      
      const data = await response.json();
      setTrendingVideos(data);
    } catch (err) {
      console.error('Error fetching trending videos:', err);
      // If trending fails, use randomized sample videos to ensure the UI looks good
      setTrendingVideos(getRandomizedSampleVideos());
    } finally {
      setIsLoadingTrending(false);
    }
  };

  // Get randomized sample videos when API fails
  const getRandomizedSampleVideos = () => {
    const sampleVideos = getSampleVideos();
    // Shuffle the sample videos array
    return sampleVideos
      .sort(() => Math.random() - 0.5)
      .slice(0, Math.floor(Math.random() * 4) + 4); // Return a random number (4-8) of videos
  };

  // Fallback sample videos in case API fails
  const getSampleVideos = () => {
    return [
      {
        videoId: "dQw4w9WgXcQ",
        title: "Rick Astley - Never Gonna Give You Up",
        author: "Rick Astley",
        lengthSeconds: 212,
        viewCount: 1234567890,
        publishedText: "10 years ago",
        videoThumbnails: [{url: `${invidious_instance}/vi/dQw4w9WgXcQ/hqdefault.jpg`}]
      },
      {
        videoId: "9bZkp7q19f0",
        title: "PSY - GANGNAM STYLE(강남스타일)",
        author: "PSY",
        lengthSeconds: 252,
        viewCount: 4600000000,
        publishedText: "11 years ago",
        videoThumbnails: [{url: `${invidious_instance}/vi/9bZkp7q19f0/hqdefault.jpg`}]
      },
      {
        videoId: "RgKAFK5djSk",
        title: "Wiz Khalifa - See You Again ft. Charlie Puth",
        author: "Wiz Khalifa",
        lengthSeconds: 237,
        viewCount: 5600000000,
        publishedText: "8 years ago",
        videoThumbnails: [{url: `${invidious_instance}/vi/RgKAFK5djSk/hqdefault.jpg`}]
      },
      {
        videoId: "JGwWNGJdvx8",
        title: "Ed Sheeran - Shape of You",
        author: "Ed Sheeran",
        lengthSeconds: 263,
        viewCount: 5800000000,
        publishedText: "6 years ago",
        videoThumbnails: [{url: `${invidious_instance}/vi/JGwWNGJdvx8/hqdefault.jpg`}]
      },
      {
        videoId: "kJQP7kiw5Fk",
        title: "Luis Fonsi - Despacito ft. Daddy Yankee",
        author: "Luis Fonsi",
        lengthSeconds: 282,
        viewCount: 7900000000,
        publishedText: "6 years ago",
        videoThumbnails: [{url: `${invidious_instance}/vi/kJQP7kiw5Fk/hqdefault.jpg`}]
      },
      {
        videoId: "FuXNumBwDOM",
        title: "Coldplay - A Sky Full Of Stars",
        author: "Coldplay",
        lengthSeconds: 267,
        viewCount: 1500000000,
        publishedText: "8 years ago",
        videoThumbnails: [{url: `${invidious_instance}/vi/FuXNumBwDOM/hqdefault.jpg`}]
      },
      {
        videoId: "09R8_2nJtjg",
        title: "Sugar - Maroon 5",
        author: "Maroon 5",
        lengthSeconds: 301,
        viewCount: 3700000000,
        publishedText: "7 years ago",
        videoThumbnails: [{url: `${invidious_instance}/vi/09R8_2nJtjg/hqdefault.jpg`}]
      },
      {
        videoId: "hT_nvWreIhg",
        title: "OneRepublic - Counting Stars",
        author: "OneRepublic",
        lengthSeconds: 274,
        viewCount: 3500000000,
        publishedText: "9 years ago",
        videoThumbnails: [{url: `${invidious_instance}/vi/hT_nvWreIhg/hqdefault.jpg`}]
      }
    ];
  };

  // Save watch history to localStorage
  const saveToHistory = (video) => {
    // Prevent duplicates by removing existing entry first
    const filteredHistory = watchHistory.filter(item => item.videoId !== video.videoId);
    
    // Add new video to the beginning of history
    const newHistory = [
      { 
        videoId: video.videoId, 
        title: video.title, 
        channelName: video.channelName || video.author,
        thumbnail: video.thumbnail || (video.videoThumbnails && video.videoThumbnails[0]?.url),
        timestamp: new Date().toISOString()
      },
      ...filteredHistory
    ].slice(0, 20); // Keep the 20 most recent videos
    
    setWatchHistory(newHistory);
    localStorage.setItem('webTopOS_youtube_history', JSON.stringify(newHistory));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    setIsLoading(true);
    setError(null);
    setIsSearching(true);
    
    try {
      // We'll use a simple search via the Invidious instance
      const response = await fetch(`${invidious_instance}/api/v1/search?q=${encodeURIComponent(searchQuery)}`);
      
      if (!response.ok) {
        throw new Error('Search failed. The Invidious instance might be down.');
      }
      
      const data = await response.json();
      
      // Filter out only video results
      const videoResults = data.filter(item => item.type === 'video');
      setSearchResults(videoResults);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search videos. The Invidious instance might be down.');
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWatchVideo = (video) => {
    setVideoId(video.videoId);
    setIsSearching(false);
    setPlayerError(false);
    
    // Save to watch history
    saveToHistory({
      videoId: video.videoId,
      title: video.title,
      channelName: video.author,
      thumbnail: video.videoThumbnails && video.videoThumbnails[0]?.url
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
  };

  const formatViews = (views) => {
    if (!views) return '';
    
    if (views > 1000000) {
      return `${(views / 1000000).toFixed(1)}M views`;
    } else if (views > 1000) {
      return `${(views / 1000).toFixed(1)}K views`;
    } else {
      return `${views} views`;
    }
  };

  const formatPublishedTime = (publishedText) => {
    return publishedText || '';
  };

  // Direct open link to youtube video (bypasses the invidious embed protection)
  const openDirectYouTubeVideo = (videoId) => {
    window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank');
  };
  
  // Function to handle connection errors
  const handlePlayerError = () => {
    setPlayerError(true);
  };

  // Video card component to reduce duplication
  const VideoCard = ({ video, onClick }) => (
    <div 
      onClick={() => onClick(video)}
      style={{
        cursor: 'pointer',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: 'rgba(30, 30, 40, 0.5)',
        transition: 'transform 0.2s',
        height: '100%'
      }}
      onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
      onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
    >
      <div style={{ position: 'relative' }}>
        <img 
          src={video.videoThumbnails && video.videoThumbnails[0]?.url} 
          alt={video.title}
          style={{ width: '100%', height: '158px', objectFit: 'cover' }}
          onError={(e) => {
            e.target.src = `${invidious_instance}/vi/${video.videoId}/hqdefault.jpg`;
          }}
        />
        {video.lengthSeconds && (
          <div style={{
            position: 'absolute',
            bottom: '5px',
            right: '5px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '2px 4px',
            borderRadius: '2px',
            fontSize: '12px'
          }}>
            {formatDuration(video.lengthSeconds)}
          </div>
        )}
      </div>
      <div style={{ padding: '12px' }}>
        <div style={{ 
          fontSize: '14px', 
          fontWeight: 'bold',
          marginBottom: '8px',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: '1.3'
        }}>
          {video.title}
        </div>
        <div style={{ fontSize: '12px', color: '#aaa' }}>
          {video.author || video.channelName}
        </div>
        <div style={{ 
          display: 'flex',
          gap: '10px',
          fontSize: '12px',
          color: '#aaa',
          marginTop: '5px'
        }}>
          {video.viewCount && <span>{formatViews(video.viewCount)}</span>}
          {video.publishedText && <span>{formatPublishedTime(video.publishedText)}</span>}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ 
      height: '100%', 
      width: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: 'black', 
      color: 'white',
      overflow: 'hidden'
    }}>
      {/* YouTube header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 20px',
        borderBottom: '1px solid #333'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: '10px'
        }}>
          <div style={{
            color: 'white',
            fontSize: '20px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer'
          }}
          onClick={() => {
            setVideoId("");
            setIsSearching(true);
          }}
          title="Home"
          >
            <i className="fab fa-youtube" style={{ color: '#ff0000' }}></i>
            <span>Privacy Tube</span>
          </div>
          
          <div style={{ 
            backgroundColor: 'transparent',
            border: '1px solid #555',
            color: '#aaa',
            fontSize: '12px',
            padding: '4px 8px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}>
            <i className="fas fa-server"></i>
            {invidious_instance.replace('https://', '')}
          </div>
        </div>
        
        <form 
          onSubmit={handleSearch} 
          style={{ 
            display: 'flex', 
            flex: 1, 
            maxWidth: '500px',
            margin: '0 20px'
          }}
        >
          <input 
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search videos..."
            style={{
              flex: 1,
              padding: '8px 15px',
              borderRadius: '20px 0 0 20px',
              border: '1px solid #333',
              backgroundColor: '#222',
              color: 'white',
              fontSize: '14px',
              outline: 'none'
            }}
          />
          <button 
            type="submit"
            style={{
              padding: '8px 15px',
              borderRadius: '0 20px 20px 0',
              border: '1px solid #333',
              borderLeft: 'none',
              backgroundColor: '#333',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            <i className="fas fa-search"></i>
          </button>
        </form>
        
        <div>
          <button 
            onClick={() => {
              fetchTrendingVideos();
              if (videoId) {
                setVideoId("");
                setIsSearching(true);
              }
            }}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '16px',
              cursor: 'pointer',
              padding: '8px'
            }}
            title="Refresh"
          >
            <i className="fas fa-sync-alt"></i>
          </button>
        </div>
      </div>
      
      {/* Main content area */}
      <div style={{ 
        flex: 1, 
        overflow: 'hidden', 
        display: 'flex',
        flexDirection: 'column'
      }}>
        {videoId ? (
          // Video player with alternative viewing options
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column' 
          }}>
            {playerError ? (
              // Error state - show when connection refused
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                backgroundColor: '#222',
                height: '300px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', color: '#ff6b6b', marginBottom: '20px' }}></i>
                <h3 style={{ marginBottom: '15px', fontSize: '18px' }}>Connection Refused</h3>
                <p style={{ marginBottom: '30px', color: '#aaa', maxWidth: '600px', lineHeight: '1.5' }}>
                  There was a problem connecting to y.com.sb. This could be due to server unavailability or connectivity issues.
                </p>
                <div style={{ display: 'flex', gap: '15px' }}>
                  <button
                    onClick={() => openDirectYouTubeVideo(videoId)}
                    style={{
                      backgroundColor: '#ff0000',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <i className="fab fa-youtube"></i>
                    Watch on YouTube
                  </button>
                  <button
                    onClick={() => {
                      setVideoId("");
                      setIsSearching(true);
                    }}
                    style={{
                      backgroundColor: '#333',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Back to Search
                  </button>
                </div>
              </div>
            ) : (
              // Regular video player
              <div style={{ 
                position: 'relative', 
                paddingTop: '56.25%', // 16:9 aspect ratio
                backgroundColor: '#000'
              }}>
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  allowFullScreen
                  title="YouTube Video Player"
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                  onError={handlePlayerError}
                />
              </div>
            )}
            
            <div style={{ padding: '15px' }}>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <button 
                  onClick={() => {
                    setVideoId("");
                    setIsSearching(true);
                  }}
                  style={{
                    backgroundColor: '#333',
                    color: 'white',
                    border: 'none',
                    padding: '8px 15px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <i className="fas fa-arrow-left"></i>
                  Back to search
                </button>
                
                {/* Alternative viewing options */}
                <button 
                  onClick={() => openDirectYouTubeVideo(videoId)}
                  style={{
                    backgroundColor: '#ff0000',
                    color: 'white',
                    border: 'none',
                    padding: '8px 15px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <i className="fab fa-youtube"></i>
                  Open in YouTube
                </button>
              </div>
              
              <div style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                padding: '15px', 
                borderRadius: '8px',
                fontSize: '14px' 
              }}>
                <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>Connection Issues?</p>
                <p>If you're experiencing connection problems, try these options:</p>
                <ol style={{ paddingLeft: '20px', margin: '10px 0', lineHeight: '1.5' }}>
                  <li>Use the "Open in YouTube" button to watch videos directly on YouTube</li>
                  <li>Wait a moment and try again as the server may be temporarily unavailable</li>
                </ol>
              </div>
            </div>
          </div>
        ) : isSearching ? (
          // Search results or home page
          <div style={{ 
            padding: '20px', 
            overflowY: 'auto', 
            flex: 1 
          }}>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '15px' }}></i>
                <p>Loading results...</p>
              </div>
            ) : error ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px',
                color: '#ff6b6b'
              }}>
                <i className="fas fa-exclamation-circle" style={{ fontSize: '24px', marginBottom: '15px' }}></i>
                <p>{error}</p>
              </div>
            ) : searchResults.length > 0 ? (
              // Search results
              <>
                <h2 style={{ marginBottom: '20px', fontSize: '18px' }}>
                  Search results for "{searchQuery}"
                </h2>
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '20px'
                }}>
                  {searchResults.map((result) => (
                    <VideoCard key={result.videoId} video={result} onClick={handleWatchVideo} />
                  ))}
                </div>
              </>
            ) : searchQuery ? (
              // No search results
              <div style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>
                <i className="fas fa-search" style={{ fontSize: '24px', marginBottom: '15px', opacity: '0.5' }}></i>
                <p>No results found for "{searchQuery}"</p>
              </div>
            ) : (
              // Home page with trending videos and history
              <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                {/* Trending videos section */}
                <div>
                  <h2 style={{ 
                    marginBottom: '20px', 
                    fontSize: '18px', 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <i className="fas fa-fire" style={{ color: '#ff4500' }}></i>
                    Popular Videos
                  </h2>
                  
                  {isLoadingTrending ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '15px' }}></i>
                      <p>Loading trending videos...</p>
                    </div>
                  ) : trendingVideos.length > 0 ? (
                    <div style={{ 
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: '20px'
                    }}>
                      {trendingVideos.map((video) => (
                        <VideoCard key={video.videoId} video={video} onClick={handleWatchVideo} />
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#aaa' }}>
                      <p>Could not load trending videos.</p>
                      <button
                        onClick={fetchTrendingVideos}
                        style={{
                          backgroundColor: '#333',
                          color: 'white',
                          border: 'none',
                          padding: '8px 15px',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          marginTop: '10px'
                        }}
                      >
                        Try Again
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Watch history section */}
                {watchHistory.length > 0 && (
                  <div>
                    <h2 style={{ 
                      marginBottom: '20px', 
                      fontSize: '18px',
                      display: 'flex', 
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <i className="fas fa-history"></i>
                      Watch History
                    </h2>
                    <div style={{ 
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: '20px'
                    }}>
                      {watchHistory.slice(0, 4).map((historyItem, index) => (
                        <VideoCard 
                          key={`history-${index}`} 
                          video={{
                            videoId: historyItem.videoId,
                            title: historyItem.title,
                            author: historyItem.channelName,
                            videoThumbnails: [{url: historyItem.thumbnail || `${invidious_instance}/vi/${historyItem.videoId}/hqdefault.jpg`}]
                          }} 
                          onClick={handleWatchVideo} 
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default YouTube; 